import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const MoneyFenSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const PublicTextSchema = Type.String({ minLength: 1, maxLength: 240 });

export const MemberDebitStateSchema = Type.Object(
  {
    status: Type.Union([
      Type.Literal("PENDING_MEMBER_DEBIT"),
      Type.Literal("CONFIRMED_CLOUD"),
      Type.Literal("MEMBER_RECEIVABLE"),
    ]),
    amountFen: MoneyFenSchema,
    workItemId: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    receivableNumber: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    message: PublicTextSchema,
    nextStep: PublicTextSchema,
    updatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type MemberDebitState = Static<typeof MemberDebitStateSchema>;

export function isMemberDebitState(value: unknown): value is MemberDebitState {
  if (!Value.Check(MemberDebitStateSchema, value)) return false;
  const hasReceivableReference = value.workItemId !== null && value.receivableNumber !== null;
  return value.status === "MEMBER_RECEIVABLE"
    ? hasReceivableReference
    : value.workItemId === null && value.receivableNumber === null;
}

export const MemberReceivableWorkItemSnapshotSchema = Type.Object(
  {
    receivableNumber: Type.String({ minLength: 1, maxLength: 64 }),
    orderNumber: Type.String({ minLength: 1, maxLength: 64 }),
    memberNumber: Type.String({ minLength: 1, maxLength: 64 }),
    memberName: Type.String({ minLength: 1, maxLength: 80 }),
    amountFen: MoneyFenSchema,
    debitStatus: Type.Union([Type.Literal("MEMBER_RECEIVABLE"), Type.Literal("CONFIRMED_CLOUD")]),
    reasonCode: Type.String({ minLength: 1, maxLength: 80 }),
    reason: Type.String({ minLength: 1, maxLength: 300 }),
    createdAt: Type.String({ format: "date-time" }),
    resolvedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    lastAttemptAt: Type.String({ format: "date-time" }),
    attemptCount: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);
export type MemberReceivableWorkItemSnapshot = Static<
  typeof MemberReceivableWorkItemSnapshotSchema
>;

export function isMemberReceivableWorkItemSnapshot(
  value: unknown,
): value is MemberReceivableWorkItemSnapshot {
  if (!Value.Check(MemberReceivableWorkItemSnapshotSchema, value)) return false;
  return value.debitStatus === "MEMBER_RECEIVABLE"
    ? value.resolvedAt === null
    : value.resolvedAt !== null;
}

export const MemberReceivableRetryRequestSchema = Type.Object(
  {
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type MemberReceivableRetryRequest = Static<typeof MemberReceivableRetryRequestSchema>;

export function isMemberReceivableRetryRequest(
  value: unknown,
): value is MemberReceivableRetryRequest {
  return Value.Check(MemberReceivableRetryRequestSchema, value);
}

export const MemberReceivableRetryResultSchema = Type.Object(
  {
    workItemId: Type.String({ minLength: 1, maxLength: 128 }),
    status: Type.Union([Type.Literal("PENDING"), Type.Literal("RESOLVED")]),
    memberDebitStatus: Type.Union([
      Type.Literal("MEMBER_RECEIVABLE"),
      Type.Literal("CONFIRMED_CLOUD"),
    ]),
    amountFen: MoneyFenSchema,
    retriedAt: Type.String({ format: "date-time" }),
    message: PublicTextSchema,
    nextStep: PublicTextSchema,
  },
  { additionalProperties: false },
);
export type MemberReceivableRetryResult = Static<typeof MemberReceivableRetryResultSchema>;

export function isMemberReceivableRetryResult(
  value: unknown,
): value is MemberReceivableRetryResult {
  if (!Value.Check(MemberReceivableRetryResultSchema, value)) return false;
  return value.status === "PENDING"
    ? value.memberDebitStatus === "MEMBER_RECEIVABLE"
    : value.memberDebitStatus === "CONFIRMED_CLOUD";
}
